import { useCallback, MouseEvent, TouchEvent } from 'react';
import type { TapLayout } from './useReaderSettings';

type TapAction = 'prev' | 'menu' | 'next';

interface UseTapZonesOptions {
  onPrev: () => void;
  onMenu: () => void;
  onNext: () => void;
  enabled?: boolean;
  layout?: TapLayout;
  edgeRatio?: number;
}

export function useTapZones({
  onPrev,
  onMenu,
  onNext,
  enabled = true,
  layout = 'leftRight-B',
  edgeRatio = 0.3,
}: UseTapZonesOptions) {
  const getTapAction = useCallback(
    (clientX: number, clientY: number, width: number, height: number): TapAction => {
      if (layout === 'topBottom') {
        // Y 軸模式：上=上一頁，下=下一頁
        const relativeY = clientY / height;
        if (relativeY < edgeRatio) return 'prev';
        if (relativeY > 1 - edgeRatio) return 'next';
        return 'menu';
      } else {
        // X 軸模式
        const relativeX = clientX / width;
        if (relativeX < edgeRatio) {
          // 左側區域
          return layout === 'leftRight-A' ? 'next' : 'prev';
        }
        if (relativeX > 1 - edgeRatio) {
          // 右側區域
          return layout === 'leftRight-A' ? 'prev' : 'next';
        }
        return 'menu';
      }
    },
    [layout, edgeRatio]
  );

  const handleTap = useCallback(
    (e: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
      if (!enabled) return;

      // 忽略來自按鈕、連結、輸入框的點擊
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.tagName === 'INPUT' ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('input')
      ) {
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();

      let clientX: number;
      let clientY: number;

      if ('touches' in e) {
        // TouchEvent - 使用 changedTouches 以支援 touchend
        const touch = e.changedTouches[0] ?? e.touches[0];
        clientX = (touch?.clientX ?? 0) - rect.left;
        clientY = (touch?.clientY ?? 0) - rect.top;
      } else {
        // MouseEvent
        clientX = e.clientX - rect.left;
        clientY = e.clientY - rect.top;
      }

      const action = getTapAction(clientX, clientY, rect.width, rect.height);

      switch (action) {
        case 'prev':
          onPrev();
          break;
        case 'menu':
          onMenu();
          break;
        case 'next':
          onNext();
          break;
      }
    },
    [enabled, getTapAction, onPrev, onMenu, onNext]
  );

  return { handleTap };
}
